/**
 * @fileoverview Saves sent one at a time, for an org settings surface that
 * saves each change the moment it is made (the Notifications and Features
 * pages, the program email times).
 *
 * Fired together, two quick changes can land on the server out of order, and
 * whichever answer arrived last used to replace the page's copy whole: one
 * switch could flip back while its own save was still in flight, and a failed
 * save rolled back to the copy its click had seen, undoing a different save
 * that had landed. Here each save is sent only once the one before it has been
 * answered, it is built on what the server last confirmed, and its answer
 * becomes the confirmed copy, so the newest answer is always the one kept. The
 * page shows its optimistic copy while anything is in flight and the confirmed
 * copy once nothing is, which is also how a refused change rolls back. The
 * same rule as the iOS org settings screens.
 */

import { useCallback, useMemo, useRef } from "react";

/**
 * @param {(confirmed: *) => void} onSettled called with the confirmed copy
 *   whenever the last save in flight has been answered, either way
 * @returns {{
 *   run: (send: (confirmed: *) => Promise<*>) => Promise<*>,
 *   confirm: (value: *) => void,
 *   busy: () => boolean,
 * }}
 */
export default function useSaveQueue(onSettled) {
  const confirmed = useRef(null);
  const chain = useRef(Promise.resolve());
  const pending = useRef(0);
  const settled = useRef(onSettled);
  settled.current = onSettled;

  /** What the server says now, from a read rather than a save. */
  const confirm = useCallback((value) => {
    confirmed.current = value;
  }, []);

  /**
   * Send one save once every earlier one has been answered. `send` receives
   * the confirmed copy to build on and resolves to the new confirmed copy,
   * taken from the server's answer. Resolves or rejects as the save did.
   */
  const run = useCallback((send) => {
    pending.current += 1;
    const request = chain.current.then(async () => {
      confirmed.current = await send(confirmed.current);
      return confirmed.current;
    });
    chain.current = request.catch(() => {});
    return request.finally(() => {
      pending.current -= 1;
      if (pending.current === 0 && confirmed.current != null) settled.current?.(confirmed.current);
    });
  }, []);

  /** Whether a save is in flight or waiting its turn. */
  const busy = useCallback(() => pending.current > 0, []);

  return useMemo(() => ({ run, confirm, busy }), [run, confirm, busy]);
}
